import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const SERVICES_DATA = [
  {
    id: "crowdfunding",
    icon: "ri-funds-line",
    image: "https://readdy.ai/api/search-image?query=crowdfunding%20campaign%20launch%20event%20with%20excited%20backers%20and%20product%20display%20on%20large%20screens%2C%20modern%20startup%20office%20showing%20funding%20progress%20bar%20reaching%20100%20percent%20goal%2C%20enthusiastic%20team%20celebrating%20milestone%20achievement%2C%20clean%20bright%20workspace%20with%20white%20and%20navy%20blue%20color%20scheme%2C%20professional%20corporate%20photography&width=800&height=500&seq=201&orientation=landscape",
  },
  {
    id: "ec",
    icon: "ri-shopping-cart-2-line",
    image: "https://readdy.ai/api/search-image?query=e-commerce%20business%20operations%20center%20with%20multiple%20screens%20showing%20online%20store%20dashboards%2C%20product%20inventory%20management%20system%2C%20order%20fulfillment%20team%20packing%20products%20in%20organized%20warehouse%2C%20clean%20modern%20facility%20with%20bright%20lighting%2C%20professional%20corporate%20photography&width=800&height=500&seq=202&orientation=landscape",
  },
  {
    id: "sns-marketing",
    icon: "ri-megaphone-line",
    image: "https://readdy.ai/api/search-image?query=social%20media%20marketing%20agency%20team%20working%20on%20digital%20advertising%20campaigns%2C%20multiple%20screens%20showing%20analytics%20dashboards%20with%20engagement%20metrics%20and%20ad%20performance%20data%2C%20creative%20content%20production%20studio%20with%20ring%20lights%20and%20camera%20equipment%2C%20modern%20bright%20office%20environment%2C%20professional%20photography&width=800&height=500&seq=203&orientation=landscape",
  },
  {
    id: "compliance",
    icon: "ri-shield-check-line",
    image: "https://readdy.ai/api/search-image?query=professional%20legal%20compliance%20consulting%20office%20with%20documents%20and%20certificates%20on%20desk%2C%20japanese%20business%20regulatory%20documents%2C%20modern%20minimalist%20corporate%20setting%20with%20clean%20white%20background%2C%20navy%20blue%20accents%2C%20high%20quality%20photography&width=800&height=500&seq=204&orientation=landscape",
  },
  {
    id: "customer-support",
    icon: "ri-customer-service-2-line",
    image: "https://readdy.ai/api/search-image?query=professional%20customer%20support%20team%20in%20bright%20modern%20office%2C%20friendly%20staff%20with%20headsets%20at%20workstations%2C%20multiple%20monitors%20showing%20customer%20data%20and%20chat%20interfaces%2C%20clean%20corporate%20environment%20with%20warm%20lighting%2C%20collaborative%20team%20atmosphere%2C%20high%20quality%20photography&width=800&height=500&seq=205&orientation=landscape",
  },
];

export default function ServicesPage() {
  const { t } = useTranslation();
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-8");
          }
        });
      },
      { threshold: 0.1 }
    );
    const children = document.querySelectorAll(".animate-on-scroll");
    children.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section ref={heroRef} className="pt-32 pb-20 md:pt-40 md:pb-28 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#1a2332] transition-colors mb-8"
            >
              <i className="ri-arrow-left-line"></i>
              <span>{t("services.backToHome")}</span>
            </Link>

            <p className="text-xs text-gray-400 tracking-[0.4em] uppercase mb-4">
              {t("services.label")} / {t("services.labelJa")}
            </p>
            <h1 className="text-4xl md:text-6xl font-black text-[#1a2332] leading-tight mb-4">
              {t("services.pageTitle")}
            </h1>
          </div>
        </div>
      </section>

      {/* Services Detail Sections */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="space-y-24 md:space-y-32">

            {/* Service 1: クラウドファンディング事業 */}
            <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                <div className="order-2 lg:order-1">
                  <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#1a2332]/5 mb-6">
                    <i className="ri-funds-line text-2xl text-[#1a2332]"></i>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-[#1a2332] mb-2">
                    {t("services.detail.s1.title")}
                  </h3>
                  <p className="text-sm text-gray-400 tracking-wider mb-6">
                    {t("services.detail.s1.titleEn")}
                  </p>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {t("services.detail.s1.desc")}
                  </p>
                  <ul className="space-y-3">
                    {[
                      t("services.detail.s1.item1"),
                      t("services.detail.s1.item2"),
                      t("services.detail.s1.item3"),
                      t("services.detail.s1.item4"),
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-gray-600">
                        <i className="ri-check-line text-[#1a2332] mt-0.5"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="order-1 lg:order-2">
                  <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-gray-100">
                    <img
                      src={SERVICES_DATA[0].image}
                      alt={t("services.detail.s1.title")}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Service 2: EC事業 */}
            <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                <div>
                  <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-gray-100">
                    <img
                      src={SERVICES_DATA[1].image}
                      alt={t("services.detail.s2.title")}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div>
                  <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#1a2332]/5 mb-6">
                    <i className="ri-shopping-cart-2-line text-2xl text-[#1a2332]"></i>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-[#1a2332] mb-2">
                    {t("services.detail.s2.title")}
                  </h3>
                  <p className="text-sm text-gray-400 tracking-wider mb-6">
                    {t("services.detail.s2.titleEn")}
                  </p>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {t("services.detail.s2.desc")}
                  </p>
                  <ul className="space-y-3">
                    {[
                      t("services.detail.s2.item1"),
                      t("services.detail.s2.item2"),
                      t("services.detail.s2.item3"),
                      t("services.detail.s2.item4"),
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-gray-600">
                        <i className="ri-check-line text-[#1a2332] mt-0.5"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Service 3: SNSマーケティング・広告運用 */}
            <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                <div className="order-2 lg:order-1">
                  <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#1a2332]/5 mb-6">
                    <i className="ri-megaphone-line text-2xl text-[#1a2332]"></i>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-[#1a2332] mb-2">
                    {t("services.detail.s3.title")}
                  </h3>
                  <p className="text-sm text-gray-400 tracking-wider mb-6">
                    {t("services.detail.s3.titleEn")}
                  </p>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {t("services.detail.s3.desc")}
                  </p>
                  <ul className="space-y-3">
                    {[
                      t("services.detail.s3.item1"),
                      t("services.detail.s3.item2"),
                      t("services.detail.s3.item3"),
                      t("services.detail.s3.item4"),
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-gray-600">
                        <i className="ri-check-line text-[#1a2332] mt-0.5"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="order-1 lg:order-2">
                  <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-gray-100">
                    <img
                      src={SERVICES_DATA[2].image}
                      alt={t("services.detail.s3.title")}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Service 4: 法規制・認証サポート */}
            <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                <div>
                  <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-gray-100">
                    <img
                      src={SERVICES_DATA[3].image}
                      alt={t("services.detail.s4.title")}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div>
                  <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#1a2332]/5 mb-6">
                    <i className="ri-shield-check-line text-2xl text-[#1a2332]"></i>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-[#1a2332] mb-2">
                    {t("services.detail.s4.title")}
                  </h3>
                  <p className="text-sm text-gray-400 tracking-wider mb-6">
                    {t("services.detail.s4.titleEn")}
                  </p>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {t("services.detail.s4.desc")}
                  </p>
                  <ul className="space-y-3">
                    {[
                      t("services.detail.s4.item1"),
                      t("services.detail.s4.item2"),
                      t("services.detail.s4.item3"),
                      t("services.detail.s4.item4"),
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-gray-600">
                        <i className="ri-check-line text-[#1a2332] mt-0.5"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Service 5: 顧客サポート */}
            <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                <div className="order-2 lg:order-1">
                  <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#1a2332]/5 mb-6">
                    <i className="ri-customer-service-2-line text-2xl text-[#1a2332]"></i>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-[#1a2332] mb-2">
                    {t("services.detail.s5.title")}
                  </h3>
                  <p className="text-sm text-gray-400 tracking-wider mb-6">
                    {t("services.detail.s5.titleEn")}
                  </p>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {t("services.detail.s5.desc")}
                  </p>
                  <ul className="space-y-3">
                    {[
                      t("services.detail.s5.item1"),
                      t("services.detail.s5.item2"),
                      t("services.detail.s5.item3"),
                      t("services.detail.s5.item4"),
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-gray-600">
                        <i className="ri-check-line text-[#1a2332] mt-0.5"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="order-1 lg:order-2">
                  <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-gray-100">
                    <img
                      src={SERVICES_DATA[4].image}
                      alt={t("services.detail.s5.title")}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Process CTA Banner */}
      <section className="py-16 md:py-20 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 flex flex-col md:flex-row items-center justify-between gap-8 bg-white rounded-2xl border border-gray-100 px-8 md:px-12 py-10">
            <div className="flex items-center gap-6">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#1a2332] text-white flex-shrink-0">
                <i className="ri-route-line text-2xl"></i>
              </div>
              <div>
                <p className="text-xs text-gray-400 tracking-[0.3em] uppercase mb-1">NEXT STEP</p>
                <h3 className="text-xl md:text-2xl font-black text-[#1a2332]">
                  お取引の流れを確認する
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  お問い合わせから販売開始まで、ステップごとに丁寧にご説明します。
                </p>
              </div>
            </div>
            <Link
              to="/process"
              className="inline-flex items-center gap-3 bg-[#1a2332] text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-[#2d3f55] transition-colors duration-300 cursor-pointer whitespace-nowrap flex-shrink-0"
            >
              <span>お取引の流れへ</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
